/**
 * qcIndex — look a WIP up in a dictionary instead of re-reading the ledger.
 *
 * WHY THIS EXISTS (Mikko, 2026-10-01: BQC5 "too slow to load and reflect"
 * once scanning started, where BQC2 to BQC4 are smooth). The tally functions in
 * qcLedger and receivingLedger answer "where does THIS WIP stand" by walking
 * EVERY scan in the list and keeping the ones with its id — and they copy the
 * whole list first to drop the undone ones. That is fine for one WIP, and BQC5
 * asks it for every WIP on the screen. While the ledger held a few hundred
 * rows nobody could tell. The 2026-09-14 userform import made it 36,030, with
 * 7,821 WIPs to ask about:
 *
 *     7,821 WIPs  x  (2 passes over 36,030 QC rows
 *                     + 2 passes over the receiving rows
 *                     + a walk down 1,261 slips)      =  ~15 SECONDS
 *
 * measured, per recompute — and a recompute happens every time ANY of the
 * lists changes: every scan recorded, and each of the five reads landing at
 * load. BQC4 has the same shape and never showed it because its lists are
 * small.
 *
 * THE FIX IS A DICTIONARY, the thing a VBA Dictionary is: group once by WIP
 * (one pass, 36,030 steps), then each WIP's tally reads only its own handful
 * of rows. The numbers do not change — `qcTally` is handed exactly the rows it
 * would have kept, and still applies its own active/cap rules to them — so
 * this is a faster route to the same answer, not a second implementation of
 * it. `qcIndex.test.ts` pins that against the originals on random data.
 *
 * KEYED ON THE ROW'S OWN wipId, LOOKED UP WITH THE TRIMMED ONE. That is the
 * comparison the tallies make (`s.wipId !== trimmed`), so a row whose id has
 * stray whitespace is invisible to the index in exactly the cases it was
 * invisible to the scan.
 *
 * Pure and I/O-free, so it can be tested without MSAL — see slipTemplate.ts on
 * that trap.
 */
import type { IQcScan, IQcTally } from "./qcLedger";
import { qcTally } from "./qcLedger";
import type { IReceivingScan, IReceiveTally } from "./receivingLedger";
import { receiveTally } from "./receivingLedger";
import type { ISlip } from "../../lib/sp/DispatchService";

/** Rows grouped by WIP, in list order within each group. */
export type ByWip<T> = ReadonlyMap<string, readonly T[]>;

/** One pass: wipId -> its rows. */
export function groupByWip<T extends { wipId: string }>(rows: readonly T[]): ByWip<T> {
  const m = new Map<string, T[]>();
  for (const r of rows) {
    const arr = m.get(r.wipId);
    if (arr) arr.push(r); else m.set(r.wipId, [r]);
  }
  return m;
}

const NONE: readonly never[] = [];

/** `qcTally` over only this WIP's own scans. Same answer, no list walk. */
export function qcTallyIn(index: ByWip<IQcScan>, wipId: string, qty: number): IQcTally {
  return qcTally(wipId, qty, index.get((wipId ?? "").trim()) ?? NONE);
}

/** `receiveTally` over only this WIP's own receiving scans. */
export function receiveTallyIn(index: ByWip<IReceivingScan>, wipId: string, qty: number): IReceiveTally {
  return receiveTally(wipId, qty, index.get((wipId ?? "").trim()) ?? NONE);
}

/**
 * wipId -> the slip `findSlipForWip` would have found: the FIRST Issued slip,
 * in list order, that names it.
 *
 * `find` stops at the first match, so the first slip to claim a WIP wins and a
 * later one never replaces it. A cancelled or draft slip is skipped the same
 * way. A WIP on two Issued slips therefore resolves to the earlier one, as it
 * always did.
 */
export function indexSlips(slips: readonly ISlip[]): ReadonlyMap<string, ISlip> {
  const m = new Map<string, ISlip>();
  for (const slip of slips) {
    if (slip.slipStatus !== "Issued") continue;
    for (const id of slip.wipIds) if (!m.has(id)) m.set(id, slip);
  }
  return m;
}

/** `findSlipForWip`, as a lookup. Blank in, null out — as before. */
export function slipFor(index: ReadonlyMap<string, ISlip>, wipId: string): ISlip | null {
  const id = (wipId ?? "").trim();
  if (!id) return null;
  return index.get(id) ?? null;
}
